import { Component, output } from '@angular/core';
import {
  ButtonComponent,
  ButtonGroupComponent
} from '@progress/kendo-angular-buttons';
import { processServiceEndpoints } from 'src/app/helpers/urls';
import { ProcessDto } from 'src/app/models/process/process-dto';
import { TaskDto } from 'src/app/models/process/task-dto';
import { SearchListConfig } from 'src/app/models/search-list-config';
import { SearchableListMicroComponent } from '../../../features/searchable-list-micro/searchable-list-micro.component';

@Component({
  selector: 'app-work-items-qa-report-target-task-selection',
  templateUrl: './work-items-qa-report-target-task-selection.component.html',
  styleUrl: './work-items-qa-report-target-task-selection.component.scss',
  imports: [ButtonGroupComponent, ButtonComponent, SearchableListMicroComponent]
})
export class WorkItemsQaReportTargetTaskSelectionComponent {
  selectedTasks: TaskDto[];
  selectedProcesses: ProcessDto[];

  readonly selectedProcessNodeIdChanged = output<number>();

  processConfig: SearchListConfig;
  taskConfig: SearchListConfig;
  taskUrl: string = processServiceEndpoints.taskEndpoint;
  processUrl: string = processServiceEndpoints.process.all;
  taskOptionSelected: boolean = true;
  processOptionSelected: boolean = false;

  currentProcess: ProcessDto;
  currentTask: TaskDto;

  constructor() {
    this.initTaskConfig();
    this.initProcessConfig();
  }

  get taskOrProcessSelected(): boolean {
    const taskIsSelected =
      this.taskOptionSelected && this.selectedTasks?.length > 0;
    const processIsSelected =
      this.processOptionSelected && this.selectedProcesses?.length > 0;
    const result = taskIsSelected || processIsSelected;
    return result;
  }

  get taskOrProcessName(): string {
    let result = '';
    if (this.taskOptionSelected && this.selectedTasks?.length > 0) {
      const task = this.selectedTasks[0];
      result = `${task.name} (${task.processName} > ${task.activityName})`;
    } else if (
      this.processOptionSelected &&
      this.selectedProcesses?.length > 0
    ) {
      result = this.selectedProcesses[0].name;
    }
    return result;
  }

  changeTaskFilter(tasks: TaskDto[]): void {
    this.selectedTasks = tasks;

    if (tasks?.length == 1) {
      this.currentTask = tasks[0];
    } else if (tasks?.length > 1) {
      this.currentTask = tasks.find(t => t.id != this.currentTask.id);
    } else {
      this.currentTask = null;
    }

    if (this.currentTask) {
      this.selectedTasks = [this.currentTask];
      this.taskConfig.placeholder = `${this.currentTask.name} (${this.currentTask.processName} > ${this.currentTask.activityName}) selected`;
    } else {
      this.selectedTasks = [];
      this.taskConfig.placeholder = 'Search tasks';
    }

    this.selectedProcessNodeIdChanged.emit(this.currentTask?.id);
  }

  changeProcessesFilter(processes: ProcessDto[]): void {
    this.selectedProcesses = processes;
    if (processes?.length == 1) {
      this.currentProcess = processes[0];
    } else if (processes?.length > 1) {
      this.currentProcess = processes.find(p => p.id != this.currentProcess.id);
    } else {
      this.currentProcess = null;
    }

    if (this.currentProcess) {
      this.selectedProcesses = [this.currentProcess];
      this.processConfig.placeholder = `${this.currentProcess.name} selected`;
    } else {
      this.selectedProcesses = [];
      this.processConfig.placeholder = 'Search processes';
    }

    this.selectedProcessNodeIdChanged.emit(this.currentProcess?.id);
  }

  onItemSwitch(): void {
    if (this.processOptionSelected) {
      this.selectedTasks = [];
      this.currentTask = null;
      this.taskConfig.placeholder =
        'Select a relevant task or process (required)';
    } else if (this.taskOptionSelected) {
      this.selectedProcesses = [];
      this.currentProcess = null;
      this.processConfig.placeholder =
        'Select a relevant task or process (required)';
    }
  }

  private initTaskConfig(): void {
    this.taskConfig = new SearchListConfig();
    this.taskConfig.virtual = true;
    this.taskConfig.placeholder =
      'Select a relevant task or process (required)';
    this.taskConfig.label = 'Task';
    this.taskConfig.key = 'tasks';
    this.taskConfig.order = '1';
    this.taskConfig.noAccessLabel =
      'No access to tasks that match search criteria';
    this.taskConfig.mode = 'none';

    this.taskConfig.primaryTextField = 'displayName';
    this.taskConfig.secondaryTextField = null;
    this.taskConfig.queryParams =
      'enabled=true&orderBy=ProcessId=asc,ActivityId=asc,TaskId=asc';

    this.taskConfig.adjustListItem = (task: TaskDto) => {
      task['displayName'] =
        `${task.name} (${task.processName} > ${task.activityName})`;
      return task;
    };
  }

  private initProcessConfig(): void {
    this.processConfig = new SearchListConfig();
    this.processConfig.virtual = true;
    this.processConfig.placeholder = 'Search process';
    this.processConfig.label = 'Process';
    this.processConfig.key = 'processes';
    this.processConfig.order = '2';
    this.processConfig.noAccessLabel =
      'No access to processes that match search criteria';
    this.processConfig.mode = 'none';

    this.processConfig.primaryTextField = 'displayName';
    this.processConfig.secondaryTextField = null;
    this.processConfig.queryParams =
      'extended=false&enabled=true&nodeConfigurationType=standard';

    this.processConfig.adjustListItem = (process: ProcessDto) => {
      process['displayName'] = `${process.name}`;
      return process;
    };
  }
}
